/* Colborne Dental | appointment CTA resolver. Added 2026-08-23.

   THE DEFECT - FIVE CAUSES, NOT ONE

   1. /pages/contact carries the client's own LeadConnector section, not the
      reusable snippet, so #cd-lead-form did not exist there at all. Every
      appointment CTA on that page pointed at /pages/contact - the page it
      was already on - so the browser reloaded and landed back at the top.
      That is the "reloads or stays in the same position" symptom, on the
      destination for two of the five campaigns.
   2. The sticky bar linked to /pages/contact on every page, including the 29
      treatment pages that already contain a form: a full document load
      instead of a scroll.
   3. The standard action bar did the same on all 26 non-campaign pages.
   4. The treatment pages set  html { scroll-behavior: smooth }  in their own
      inline CSS. That silently turns EVERY window.scrollTo() into a smooth
      animation - and a smooth animation is abandoned outright when the
      LeadConnector iframe finishes loading, resizes, and shifts the layout
      underneath it. Measured on /products/emergency-dentistry: a plain
      window.scrollTo(0, 4061) moved the page 0px, while the identical call
      with behavior:'instant' moved it to 4061. No amount of CSS
      scroll-margin fixes that.
   5. Arriving from another page with #cd-lead-form in the URL, the form's
      position is still moving: the hero image, the reveal animations and
      the LeadConnector iframe all change height after the anchor already
      exists. A one-shot scroll lands somewhere that is correct for about
      400ms. Measured: the form ended up 770px ABOVE the viewport.

   So this file guarantees the anchor exists, keeps it unique, rewrites the
   theme's own header menu link, and owns the scroll end to end: it
   neutralises the inherited scroll-behavior for the duration of the
   movement, animates the distance itself, then re-checks the target every
   200ms until it stops moving - aborting the moment the visitor scrolls.

   It never touches tel: links and never fires a lead event. */
(function () {
  'use strict';

  if (window.CDCta) { return; }

  var ID = 'cd-lead-form';
  var CONTACT_PATH = '/pages/contact';
  var GAP = 20;              /* breathing room below the sticky header */
  var DUR = 480;             /* animation window, ms */
  var SETTLE_MS = 4000;      /* how long to keep correcting for late layout */

  /* ---------- header offset ---------- */
  function headerHeight() {
    var h = 0;
    var sel = ['.shopify-section-group-header-group .header-wrapper', '.section-header', '#shopify-section-header', 'header'];
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (!el) { continue; }
      var cs = window.getComputedStyle(el);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        h = Math.ceil(el.getBoundingClientRect().height);
        break;
      }
      if (!h) { h = Math.ceil(el.getBoundingClientRect().height); }
    }
    if (!h || h > 240) { h = 96; }
    return h;
  }

  function syncHeaderVar() {
    document.documentElement.style.setProperty('--cd-header-h', headerHeight() + 'px');
  }

  /* ---------- anchor guarantee + uniqueness ---------- */
  function dedupe() {
    var all = document.querySelectorAll('#' + ID);
    for (var i = 1; i < all.length; i++) {
      all[i].id = ID + '-' + (i + 1);
    }
  }

  /* On /pages/contact the form belongs to a client-owned custom section.
     Adopt its wrapper so the anchor exists without editing their template. */
  function ensureAnchor() {
    dedupe();
    var found = document.getElementById(ID);
    if (found) { return found; }

    var frame = document.querySelector('iframe[src*="leadconnectorhq.com/widget/form"]');
    if (!frame) { return null; }

    var host = frame.closest('.shopify-section') || frame.parentElement;
    if (!host) { return null; }

    host.id = ID;
    host.classList.add('cd-lead-anchor');
    return host;
  }

  function target() {
    return document.getElementById(ID);
  }

  /* ---------- geometry ---------- */
  function maxScroll() {
    return Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
  }

  function targetY(el) {
    var top = el.getBoundingClientRect().top + window.pageYOffset;
    return Math.max(0, Math.min(Math.round(top - headerHeight() - GAP), maxScroll()));
  }

  /* ---------- accessible focus ----------
     Never focus inside the cross-origin LeadConnector iframe. Prefer a real
     heading. The adopted client section has none, so label the wrapper
     rather than letting a screen reader read its inline stylesheet. */
  function focusTarget(el) {
    var head = el.querySelector('.cd-lead__title, h1, h2, h3');
    var node = head || el;
    if (node === el && !el.getAttribute('aria-label')) {
      el.setAttribute('role', 'region');
      el.setAttribute('aria-label', 'Appointment request form');
    }
    if (!node.hasAttribute('tabindex')) { node.setAttribute('tabindex', '-1'); }
    try { node.focus({ preventScroll: true }); } catch (e) { try { node.focus(); } catch (e2) {} }
  }

  /* ---------- scroll ----------
     Two independent defences against the page's inherited
     html { scroll-behavior: smooth }: an explicit behavior:'instant' where
     the browser supports it, and an inline override held for the duration
     of the movement for the browsers that do not. */
  var busy = null;
  var sbPrev = null;
  var sbLock = 0;
  var settleTimer = null;

  function lockScrollBehavior() {
    var de = document.documentElement;
    if (sbLock === 0) {
      sbPrev = de.style.scrollBehavior;
      de.style.scrollBehavior = 'auto';
    }
    sbLock++;
  }

  function unlockScrollBehavior() {
    sbLock = sbLock > 0 ? sbLock - 1 : 0;
    if (sbLock === 0) { document.documentElement.style.scrollBehavior = sbPrev || ''; }
  }

  function rawScroll(y) {
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, y);
    }
    /* last resort for engines that ignored both */
    if (Math.abs(window.pageYOffset - y) > 2) {
      try { document.documentElement.scrollTop = y; } catch (e2) {}
    }
  }

  function enforce(el) {
    var want = targetY(el);
    if (Math.abs(window.pageYOffset - want) > 4) { rawScroll(want); }
    return want;
  }

  /* Hold the target while the page finishes laying itself out. Images, the
     reveal animations and the LeadConnector iframe all change height after
     the anchor exists, so a single scroll is not enough. Give up as soon as
     the target stops moving, when the budget runs out, or - most
     importantly - the instant the visitor scrolls for themselves. */
  function settle(el) {
    var last = -1, stable = 0, t0 = Date.now(), stopped = false;

    function abort() { stopped = true; }

    function done() {
      if (settleTimer) { clearInterval(settleTimer); settleTimer = null; }
      window.removeEventListener('wheel', abort);
      window.removeEventListener('touchstart', abort);
      window.removeEventListener('keydown', abort);
      unlockScrollBehavior();
    }

    window.addEventListener('wheel', abort, { passive: true });
    window.addEventListener('touchstart', abort, { passive: true });
    window.addEventListener('keydown', abort);

    if (settleTimer) { clearInterval(settleTimer); }
    settleTimer = setInterval(function () {
      if (stopped || !document.body.contains(el)) { done(); return; }
      var want = targetY(el);
      if (Math.abs(window.pageYOffset - want) > 4) {
        rawScroll(want);
        stable = 0; last = want;
      } else if (want === last) {
        stable++;
      } else {
        stable = 0; last = want;
      }
      if (stable >= 3 || Date.now() - t0 > SETTLE_MS) { done(); }
    }, 200);
  }

  function goTo(el, pushHash) {
    if (!el) { return; }
    syncHeaderVar();
    lockScrollBehavior();

    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    var start = window.pageYOffset;
    var end = targetY(el);

    if (busy) { cancelAnimationFrame(busy); busy = null; }

    if (reduce || Math.abs(end - start) < 2) {
      rawScroll(end);
    } else {
      var t0 = (window.performance && performance.now) ? performance.now() : Date.now();
      (function step() {
        var now = (window.performance && performance.now) ? performance.now() : Date.now();
        var p = Math.min(1, (now - t0) / DUR);
        var e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        rawScroll(Math.round(start + (end - start) * e));
        if (p < 1) { busy = requestAnimationFrame(step); } else { busy = null; }
      })();
    }

    /* Only add a history entry when the hash actually changes, so repeated
       CTA clicks do not stack up entries the visitor has to press Back
       through to leave the page. */
    if (pushHash && window.location.hash !== '#' + ID && window.history && window.history.pushState) {
      try { window.history.pushState(null, '', '#' + ID); } catch (e) {}
    }

    setTimeout(function () {
      if (busy) { cancelAnimationFrame(busy); busy = null; }
      enforce(el);
      focusTarget(el);
      settle(el);
    }, DUR + 120);
  }

  /* ---------- href helpers ---------- */
  function contactUrl() {
    /* carry the current query string so UTMs and click ids survive the hop */
    var q = window.location.search || '';
    return CONTACT_PATH + q + '#' + ID;
  }

  function isAppointmentLink(a) {
    if (!a) { return false; }
    var href = a.getAttribute('href') || '';
    if (href.indexOf('tel:') === 0 || href.indexOf('mailto:') === 0) { return false; }
    if (a.hasAttribute('data-cd-book')) { return true; }
    if (href === '#' + ID) { return true; }
    var u;
    try { u = new URL(a.href, window.location.origin); } catch (e) { return false; }
    if (u.origin !== window.location.origin) { return false; }
    return u.pathname.replace(/\/$/, '') === CONTACT_PATH;
  }

  /* Rewrite hrefs so the resolved destination is correct without JS click
     handling, and so the status bar shows the real target on hover. */
  function normaliseHrefs() {
    var here = !!target();
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (!isAppointmentLink(a)) { continue; }
      if (a.getAttribute('data-cd-cta') === '1') { continue; }
      a.setAttribute('data-cd-cta', '1');
      a.setAttribute('href', here ? '#' + ID : contactUrl());
    }
  }

  /* ---------- click handling ---------- */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return; }
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !isAppointmentLink(a)) { return; }

    var el = ensureAnchor();
    if (!el) { return; }          /* no form here - let it navigate to Contact */
    e.preventDefault();
    goTo(el, true);
  }, false);

  /* ---------- arriving with the hash from another page ---------- */
  function handleIncomingHash() {
    if (window.location.hash !== '#' + ID) { return; }
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var el = ensureAnchor();
      if (el) { clearInterval(t); goTo(el, false); }
      else if (tries > 40) { clearInterval(t); }
    }, 100);
  }

  function pass() {
    syncHeaderVar();
    ensureAnchor();
    normaliseHrefs();
  }

  function init() {
    pass();
    handleIncomingHash();
    setTimeout(pass, 700);
    setTimeout(pass, 1800);
    window.addEventListener('resize', syncHeaderVar, { passive: true });
    window.addEventListener('hashchange', function () {
      if (window.location.hash === '#' + ID) {
        var el = ensureAnchor();
        if (el) { goTo(el, false); }
      }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }

  window.CDCta = {
    ensureAnchor: ensureAnchor,
    goTo: goTo,
    targetY: targetY,
    normaliseHrefs: normaliseHrefs,
    headerHeight: headerHeight
  };
})();
